import { useMemo, useState } from 'react'
import { copyText } from '../lib/clipboard'
import { extractDomains, formatGroups, groupBySuffix } from '../lib/refine'
import type { DomainGroup } from '../lib/refine'

/**
 * 提取器：把聊天记录 / 通知 / 表格导出里夹着的域名挑出来，按后缀分好竖排列干净，
 * 一键复制就能贴进「草稿表格」或「入库」页。
 *
 * 规则是宁可少挑、不要挑错：IP、日期、版本号、文件名一律不进。
 */
export function Extractor() {
  const [text, setText] = useState('')
  const [groups, setGroups] = useState<DomainGroup[]>([])
  const [ran, setRan] = useState(false)
  const [copied, setCopied] = useState<null | string>(null)

  const found = useMemo(() => extractDomains(text), [text])
  const total = groups.reduce((n, g) => n + g.items.length, 0)

  function extract() {
    setGroups(groupBySuffix(found))
    setCopied(null)
    setRan(true)
  }

  async function copy(key: string, value: string) {
    setCopied((await copyText(value)) ? key : 'fail')
  }

  const label = (key: string, okText: string) =>
    copied === 'fail' ? '复制失败，请手动选' : copied === key ? `${okText} ✓` : okText

  return (
    <section>
      <header className="page-head compact">
        <h1>提取器</h1>
      </header>
      <div className="card">
        <div className="refine-grid">
          <div>
            <textarea
              rows={10}
              aria-label="粘贴任意文案，系统只挑出里面的域名"
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                setRan(false)
              }}
              placeholder={'把聊天记录、通知、表格导出整段粘进来，例如\n✅ nioic.cc → 156.234.67.107\n"1.nioic.cc\n2.orzz137.top"'}
            />
            <div className="row spread">
              <span className="muted" role="status">
                {text.trim() ? `识别到 ${found.length} 个域名（已去重）` : '粘贴后自动识别'}
              </span>
              <div className="row">
                <button
                  type="button"
                  onClick={() => {
                    setText('')
                    setGroups([])
                    setRan(false)
                    setCopied(null)
                  }}
                  disabled={!text}
                >
                  清空
                </button>
                <button type="button" className="primary" onClick={extract} disabled={found.length === 0}>
                  提取域名
                </button>
              </div>
            </div>
          </div>

          <div className="refine-out">
            {!ran && <p className="muted">左边粘文案、点「提取域名」，这里就按后缀分好组。</p>}
            {ran && total === 0 && <p className="muted">没挑出域名。检查一下粘贴的内容里是否真有域名。</p>}
            {ran && total > 0 && (
              <>
                <div className="row spread refine-head">
                  <span className="muted">
                    共 {total} 个 · {groups.length} 组
                  </span>
                  <button type="button" onClick={() => void copy('all', formatGroups(groups))}>
                    {label('all', '复制全部')}
                  </button>
                </div>
                <div className="refine-cols">
                  {groups.map((g) => (
                    <section className="refine-col" key={g.suffix}>
                      <header>
                        <b className="mono">{g.suffix}</b>
                        <span className="muted">{g.items.length}</span>
                        <button
                          type="button"
                          className="link"
                          onClick={() => void copy(g.suffix, g.items.join('\n'))}
                          title={`只复制 ${g.suffix} 这一组`}
                        >
                          {label(g.suffix, '复制')}
                        </button>
                      </header>
                      <div className="mono refine-list">
                        {g.items.map((d) => (
                          <div key={d}>{d}</div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
